import { Injectable } from '@angular/core';
import { Observable, pipe, combineLatest, of, forkJoin, from } from 'rxjs';
import { map, switchMap, filter, tap, take } from 'rxjs/operators';
import { DateTime, Duration } from 'luxon';

import { Fetch } from '@app/models/fetch';
import { Cache } from '@app/models/cache';
import { Device } from '@app/models/devices/devices.model';
import { Notification } from '@app/models/notifications/notifications.model';
import { config } from '@config/config';
import { Location } from '@app/models/locations/locations.model';

@Injectable({
    providedIn: 'root',
})
export class DeviceService {
    baseUrl = '/devices';
    cacheName = 'devices';

    constructor(private fetch: Fetch, private cache: Cache) {
        this.cache.register(this.cacheName, Device, 60);
    }

    get(id: string) {
        return this.cache.getOne<Device>(this.cacheName, id, this.fetch.getOne<Device>(this.baseUrl, id));
    }

    getFromLocation(loc: Location): Observable<{ br: Device; ws: Device }> {
        return this.cache
            .getAll<Device>(
                this.cacheName,
                this.fetch.getAll<Device>(this.baseUrl, { list_shared: true })
            )
            .pipe(
                map((models) => ({
                    br: models.find((dev) => dev.location_id === loc.id && dev.type === config.deviceTypes.BRIDGE),
                    ws: models.find((dev) => dev.location_id === loc.id && dev.type === config.deviceTypes.WS),
                }))
            );
    }

    getAll() {
        return this.cache.getAll<Device>(this.cacheName, this.fetch.getAll<Device>(this.baseUrl));
    }

    joinNotifications(notifs: Notification[]): Observable<any> {
        return this.fetch
            .getSome<Device>(
                this.baseUrl,
                notifs.map((notif) => notif.device_id)
            )
            .req.pipe(
                map((devices) => {
                    for (let i = 0; i < notifs.length; i++) {
                        notifs[i].device = devices[i];
                    }
                    return notifs;
                })
            );
    }

    joinNotificationsPipe() {
        return pipe(
            switchMap((notifs: Notification[]) => {
                if (notifs.length === 0) {
                    return of(notifs);
                }
                return this.joinNotifications(notifs);
            })
        );
    }

    joinLocations(locs: Location[]): Observable<Location[]> {
        return forkJoin(
            locs.map((loc) =>
                this.getFromLocation(loc).pipe(
                    map((devs) => {
                        loc.bridge = devs.br;
                        loc.ws = devs.ws;
                        return loc;
                    })
                )
            )
        );
    }

    joinLocationPipe() {
        return pipe(
            switchMap((loc: Location) => this.joinLocations([loc])),
            map((loc) => loc[0])
        );
    }

    joinLocationsPipe() {
        return pipe(switchMap((locs: Location[]) => this.joinLocations(locs)));
    }

    getDeviceType(id: string) {
        return this.get(id).pipe(map((device) => device));
    }
}
